import React, { useEffect, useMemo, useState } from 'react';
import { adminGetSuggestions, adminSuggestionAction, deleteSuggestion, restoreSuggestion } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Lightbulb, CheckCircle2, Clock, XCircle, FlaskConical, Code2, Sparkles, ChevronRight, Mail, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  pending:       { label: 'Pending review',       tone: 'bg-slate-100 text-slate-700 border-slate-200',     Icon: Clock },
  approved:      { label: 'Approved',              tone: 'bg-blue-50 text-blue-700 border-blue-200',         Icon: CheckCircle2 },
  in_dev:        { label: 'In development',        tone: 'bg-indigo-50 text-indigo-700 border-indigo-200',   Icon: Code2 },
  admin_test:    { label: 'Awaiting admin test',   tone: 'bg-amber-50 text-amber-800 border-amber-200',      Icon: FlaskConical },
  customer_test: { label: 'Awaiting customer test',tone: 'bg-teal-50 text-teal-800 border-teal-200',         Icon: Sparkles },
  done:          { label: 'Live',                  tone: 'bg-green-50 text-green-700 border-green-200',      Icon: CheckCircle2 },
  rejected:      { label: 'Rejected',              tone: 'bg-rose-50 text-rose-700 border-rose-200',         Icon: XCircle },
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${meta.tone}`}>
      <Icon className="h-3.5 w-3.5" />
      {meta.label}
    </span>
  );
};

// What admin actions are available given the current status. Mirrors
// the backend VALID_TRANSITIONS table so the UI never offers an action
// the API will reject.
const ACTIONS_BY_STATUS = {
  pending:       [{ key: 'approve', label: 'Approve', primary: true }, { key: 'reject', label: 'Reject', danger: true }],
  approved:      [{ key: 'in-dev', label: 'Mark in development' }, { key: 'ready-admin-test', label: 'Ready for me to test', primary: true, mail: true }, { key: 'reject', label: 'Reject', danger: true }],
  in_dev:        [{ key: 'ready-admin-test', label: 'Ready for me to test', primary: true, mail: true }, { key: 'reject', label: 'Reject', danger: true }],
  admin_test:    [{ key: 'ready-customer-test', label: 'Send to customer', primary: true, mail: true }, { key: 'in-dev', label: 'Send back to dev' }],
  customer_test: [{ key: 'close', label: 'Mark live' }, { key: 'ready-admin-test', label: 'Pull back to admin test' }],
  done:          [],
  rejected:      [],
};

const AdminSuggestions = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('open');

  // Action dialog
  const [actionTarget, setActionTarget] = useState(null); // { suggestion, action }
  const [actionNote, setActionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const refresh = () => adminGetSuggestions().then(setItems);

  const handleDelete = async (s) => {
    try {
      await deleteSuggestion(s.id);
      const snapshot = items;
      setItems((list) => list.filter(x => x.id !== s.id));
      toast.success(`Suggestion "${s.title}" deleted`, {
        duration: 6000,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await restoreSuggestion(s.id);
              setItems(snapshot);
              toast.success('Restored');
            } catch {
              toast.error('Restore failed');
              refresh();
            }
          },
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const counts = useMemo(() => ({
    all: items.length,
    open: items.filter(s => !['done', 'rejected'].includes(s.status)).length,
    pending: items.filter(s => s.status === 'pending').length,
    admin_test: items.filter(s => s.status === 'admin_test').length,
    customer_test: items.filter(s => s.status === 'customer_test').length,
    closed: items.filter(s => ['done', 'rejected'].includes(s.status)).length,
  }), [items]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'all': return items;
      case 'open': return items.filter(s => !['done', 'rejected'].includes(s.status));
      case 'pending': return items.filter(s => s.status === 'pending');
      case 'admin_test': return items.filter(s => s.status === 'admin_test');
      case 'customer_test': return items.filter(s => s.status === 'customer_test');
      case 'closed': return items.filter(s => ['done', 'rejected'].includes(s.status));
      default: return items;
    }
  }, [items, filter]);

  const openAction = (suggestion, action) => {
    setActionTarget({ suggestion, action });
    setActionNote('');
  };

  const submitAction = async () => {
    if (!actionTarget) return;
    setSubmitting(true);
    try {
      await adminSuggestionAction(
        actionTarget.suggestion.id,
        actionTarget.action.key,
        actionNote.trim(),
      );
      toast.success('Updated');
      setActionTarget(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading suggestions...</div>;

  const tabs = [
    { key: 'open',          label: 'Open',           count: counts.open },
    { key: 'pending',       label: 'Pending review', count: counts.pending },
    { key: 'admin_test',    label: 'My queue',       count: counts.admin_test },
    { key: 'customer_test', label: 'With customer',  count: counts.customer_test },
    { key: 'closed',        label: 'Closed',         count: counts.closed },
    { key: 'all',           label: 'All',            count: counts.all },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Customer Suggestions"
        description="Approve or reject incoming ideas, hand off to dev, then loop the customer back in for sign-off."
      />

      <div className="flex flex-wrap items-center gap-2 text-sm">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            data-testid={`suggestion-filter-${t.key}`}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
              filter === t.key
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {t.label} <span className="text-xs opacity-70">({t.count})</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="p-12 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md">
          {items.length === 0
            ? 'No suggestions submitted yet.'
            : 'No suggestions match this filter.'}
        </div>
      )}

      <div className="space-y-4">
        {filtered.map(s => {
          const actions = ACTIONS_BY_STATUS[s.status] || [];
          return (
            <div key={s.id} data-testid={`admin-suggestion-${s.id}`} className="bg-white border border-slate-200 rounded-md p-5 overflow-hidden">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-start sm:justify-between gap-3 sm:gap-4 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs font-mono text-slate-500">{s.id}</span>
                    <StatusPill status={s.status} />
                    <span className="text-[11px] uppercase tracking-wider text-slate-400">{s.area || 'general'}</span>
                  </div>
                  <h3 className="font-display uppercase text-base text-slate-900 tracking-wide mt-1 break-words">{s.title}</h3>
                  <div className="text-xs text-slate-500 mt-0.5">From <strong>{s.customerName}</strong></div>
                </div>
                {(
                  <div className="flex flex-wrap gap-2 sm:shrink-0 sm:max-w-[60%]">
                    {actions.map(a => (
                      <Button
                        key={a.key}
                        size="sm"
                        variant={a.primary ? 'default' : 'outline'}
                        onClick={() => openAction(s, a)}
                        className={`${a.primary ? 'bg-slate-900 hover:bg-slate-800' : a.danger ? 'border-rose-200 text-rose-700 hover:bg-rose-50' : ''} whitespace-normal h-auto py-1.5 text-xs sm:text-sm`}
                        data-testid={`action-${s.id}-${a.key}`}
                      >
                        {a.mail && <Mail className="h-3.5 w-3.5 mr-1.5 shrink-0" />}
                        <span className="text-left">{a.label}</span>
                      </Button>
                    ))}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(s)}
                      className="border-rose-200 text-rose-700 hover:bg-rose-50 whitespace-normal h-auto py-1.5 text-xs sm:text-sm"
                      data-testid={`admin-suggestion-delete-${s.id}`}
                      title="Delete suggestion"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5 shrink-0" />
                      <span className="text-left">Delete</span>
                    </Button>
                  </div>
                )}
              </div>
              {s.description && (
                <p className="text-sm text-slate-700 whitespace-pre-wrap mb-3">{s.description}</p>
              )}
              {s.history?.length > 0 && (
                <details className="text-xs text-slate-500">
                  <summary className="cursor-pointer hover:text-slate-700 select-none flex items-center gap-1">
                    <ChevronRight className="h-3 w-3" /> History ({s.history.length})
                  </summary>
                  <ul className="mt-2 ml-4 space-y-1">
                    {s.history.map((h, i) => (
                      <li key={i} className="font-mono">
                        <span className="text-slate-400">{new Date(h.at).toLocaleString()}</span> · <span className="text-slate-700">{h.event}</span>{h.by ? ` · ${h.by}` : ''}{h.note ? ` — ${h.note}` : ''}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          );
        })}
      </div>

      {/* Action dialog */}
      <Dialog open={!!actionTarget} onOpenChange={(o) => !o && setActionTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{actionTarget?.action.label}</DialogTitle>
            <DialogDescription>
              {actionTarget && (
                <>
                  <span className="font-semibold text-slate-900">{actionTarget.suggestion.title}</span>
                  {' · '}
                  <span className="text-slate-600">{actionTarget.suggestion.customerName}</span>
                  {actionTarget.action.mail && (
                    <span className="block mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                      {actionTarget.action.key === 'ready-admin-test'
                        ? 'A branded email will be sent to all admins.'
                        : 'A branded email will be sent to the customer.'}
                    </span>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Textarea
              rows={4}
              value={actionNote}
              onChange={e => setActionNote(e.target.value)}
              placeholder={actionTarget?.action.danger
                ? 'Why is this being rejected? (visible to the customer)'
                : 'Optional note for the audit trail (and email body)'}
              data-testid="action-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionTarget(null)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submitAction}
              disabled={submitting}
              className={actionTarget?.action.danger ? 'bg-rose-700 hover:bg-rose-800' : 'bg-slate-900 hover:bg-slate-800'}
              data-testid="confirm-action-btn"
            >
              {submitting ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSuggestions;
